<script setup lang="ts">
import { Components } from '@/api/generated/api';
import { useSchoolClassAccess } from '@/composables/schoolClassAccess/useSchoolClassAccess';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { usePermissions } from '@/composables/usePermissions';
import { useRouteContext } from '@/composables/useRouteContext';
import { useWhoAmI } from '@/composables/useWhoAmI';
import UserAccessRightDisplayListItem from '@/core/list-items/UserAccessRightDisplayListItem.vue';
import UserAccessRightListItem from '@/core/list-items/UserAccessRightListItem.vue';
import { IUserWithPermissions } from '@/interfaces';
import { useContainerTranslations } from '@/schoolClasses/useContainerTranslations';
import { provideViewedSchoolClass } from '@/schoolClasses/useSchoolClassType';
import { isClassAccessRole } from '@/utils/role.utils';
import { getAccessRightUserKey } from 'common/utils/access.right.utils';
import { toRecord } from 'common/utils/array.utils';
import { nextTick, ref, toRefs, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { upsertAnyAccessRights } = useSchoolClassAccess();
const { whoAmI } = useWhoAmI();
const { canManageAccessRights } = usePermissions();
const { confirm } = useConfirmDialog();
const { isClassContext } = useRouteContext();
const { t } = useI18n();

const emit = defineEmits<{
  'update:users-with-access': [value: IUserWithPermissions[]];
  'update:isValid': [value: boolean];
}>();

const props = defineProps<{
  availableSchoolSubjectGroups: Components.Schemas.SchoolSubjectGroupDto[];
  availableSchoolSubjects: Components.Schemas.SchoolSubjectDto[];
  mode: 'archive' | 'edit';
  schoolClass: Components.Schemas.SchoolClassDto;
  usersWithAccess: IUserWithPermissions[];
}>();
const { schoolClass, usersWithAccess, availableSchoolSubjects, availableSchoolSubjectGroups } = toRefs(props);

// the edited class is not necessarily the routed one, so nested access-right forms are told which it is
provideViewedSchoolClass(() => schoolClass.value);
const { tContainer } = useContainerTranslations(schoolClass);

const usersWithAccessInternal = ref<IUserWithPermissions[]>([]);
const validityByUserKey = ref<Record<string, boolean>>({});
const justAddedUserId = ref<number | null>(null);
const newUserKeys = ref<Set<string>>(new Set());

const getUserAccessKey = (userWithAccess: IUserWithPermissions): string => getAccessRightUserKey(userWithAccess);

const allUsersValid = computed(() => {
  return usersWithAccessInternal.value.every(
    (userWithAccess) => validityByUserKey.value[getUserAccessKey(userWithAccess)] !== false,
  );
});

const emitCurrentValidity = (): void => emit('update:isValid', allUsersValid.value);
const syncValidityByUserKey = (): void => {
  const prevValidityByUserKey = validityByUserKey.value;
  validityByUserKey.value = toRecord(
    usersWithAccessInternal.value,
    (userWithAccess) => getUserAccessKey(userWithAccess),
    (userWithAccess) => prevValidityByUserKey[getUserAccessKey(userWithAccess)] ?? true,
  );
};

const handleUpdateUsers = (teamMembers: IUserWithPermissions[]): void => {
  emit('update:users-with-access', teamMembers);
};

const handleSubmit = async (): Promise<void> => {
  await upsertAnyAccessRights(schoolClass.value.id, usersWithAccessInternal.value);
};

const onUserUpdated = (user: IUserWithPermissions): void => {
  const updatedUserKey = getUserAccessKey(user);
  const userIndex = usersWithAccessInternal.value.findIndex((u) => getUserAccessKey(u) === updatedUserKey);
  if (userIndex === -1) return;
  usersWithAccessInternal.value[userIndex] = user;
  handleUpdateUsers(usersWithAccessInternal.value);
  emitCurrentValidity();
};

const onUserRemoved = async (user: IUserWithPermissions): Promise<void> => {
  const confirmed = await confirm({
    title: t('accessRights.removeTitle'),
    description: tContainer('teamMemberRemoveDescription'),
    confirmButtonLabel: t('accessRights.removeButton'),
    declineButtonLabel: t('general.cancel'),
  });
  if (!confirmed) return;
  const removedUserKey = getUserAccessKey(user);
  usersWithAccessInternal.value = usersWithAccessInternal.value.filter((u) => getUserAccessKey(u) !== removedUserKey);
  syncValidityByUserKey();
  emitCurrentValidity();

  handleUpdateUsers(usersWithAccessInternal.value);
};

const onValidityChanged = (userKey: string, val: boolean): void => {
  validityByUserKey.value = {
    ...validityByUserKey.value,
    [userKey]: val,
  };
  emitCurrentValidity();
};

const isDisabled = (user: IUserWithPermissions): boolean => {
  return (isClassContext.value && whoAmI.value?.id === user.user.id) || !isClassAccessRole(user.userRoleType);
};

const hideRemoveButton = (user: IUserWithPermissions): boolean => {
  return isClassContext.value && whoAmI.value?.id === user.user.id && isClassAccessRole(user.userRoleType);
};

watch(
  usersWithAccess,
  () => {
    usersWithAccessInternal.value = usersWithAccess.value;
    syncValidityByUserKey();
    emitCurrentValidity();
  },
  { immediate: true },
);

const addUser = async (user: IUserWithPermissions): Promise<void> => {
  justAddedUserId.value = user.user.id;
  const userKey = getUserAccessKey(user);
  newUserKeys.value = new Set([...newUserKeys.value, userKey]);
  usersWithAccessInternal.value = [
    user,
    ...usersWithAccessInternal.value.filter((u) => getUserAccessKey(u) !== userKey),
  ];
  syncValidityByUserKey();
  handleUpdateUsers(usersWithAccessInternal.value);

  await nextTick();
  justAddedUserId.value = null;
};

defineExpose({
  submit: handleSubmit,
  addUser,
});
</script>

<template>
  <form class="min-w-0" @submit.prevent="handleSubmit">
    <template v-for="tenantUser in usersWithAccessInternal" :key="getUserAccessKey(tenantUser)">
      <UserAccessRightListItem
        v-if="canManageAccessRights"
        :available-school-subject-groups="availableSchoolSubjectGroups"
        :available-school-subjects="availableSchoolSubjects"
        :disabled="isDisabled(tenantUser)"
        :hide-remove-button="hideRemoveButton(tenantUser)"
        :is-new-access-right="newUserKeys.has(getUserAccessKey(tenantUser))"
        :mode="mode"
        :start-open="justAddedUserId === tenantUser.user.id"
        :user="tenantUser"
        type="remove"
        @click:remove="onUserRemoved"
        @update:user="onUserUpdated"
        @update:is-valid="(val) => onValidityChanged(getUserAccessKey(tenantUser), val)"
      />
      <UserAccessRightDisplayListItem v-else :user="tenantUser" />
    </template>
  </form>
</template>
